import { useEffect, useRef, useCallback, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import CookieBanner from "./components/CookieBanner";
import WhatsAppWidget from "./components/WhatsAppWidget";
import AuthGate from "./pages/AuthGate";
// Landing rămâne eager — e pagina cea mai vizitată, nu trebuie să aștepte un chunk separat
import Landing from "./pages/Landing";

// Restul paginilor se încarcă la cerere (code-splitting per rută) — homepage-ul
// nu mai descarcă inutil codul de Dashboard, Admin, calculatoare etc.
const Login               = lazy(() => import("./pages/Login"));
const Signup               = lazy(() => import("./pages/Signup"));
const ForgotPassword       = lazy(() => import("./pages/ForgotPassword"));
const Dashboard             = lazy(() => import("./pages/Dashboard"));
const Profile               = lazy(() => import("./pages/Profile"));
const ResetPassword         = lazy(() => import("./pages/ResetPassword"));
const Termeni               = lazy(() => import("./pages/Termeni"));
const Metodologie           = lazy(() => import("./pages/Metodologie"));
const Confidentialitate     = lazy(() => import("./pages/Confidentialitate"));
const Cookies               = lazy(() => import("./pages/Cookies"));
const Admin                 = lazy(() => import("./pages/Admin"));
const Blog                  = lazy(() => import("./pages/Blog"));
const BlogPost              = lazy(() => import("./pages/BlogPost"));
const BlogAdmin             = lazy(() => import("./pages/BlogAdmin"));
const NotFound               = lazy(() => import("./pages/NotFound"));
const PaymentSuccess         = lazy(() => import("./pages/PaymentSuccess"));
const MyReports             = lazy(() => import("./pages/MyReports"));
const Calculatoare           = lazy(() => import("./pages/Calculatoare"));
const CalculatorSalariu     = lazy(() => import("./pages/calculatoare/CalculatorSalariu"));
const CalculatorDividende   = lazy(() => import("./pages/calculatoare/CalculatorDividende"));
const CalculatorMicroSRL     = lazy(() => import("./pages/calculatoare/CalculatorMicroSRL"));
const CalculatorTVA         = lazy(() => import("./pages/calculatoare/CalculatorTVA"));

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minute

function AutoLogout() {
  const nav = useNavigate();
  const timer = useRef(null);

  const reset = useCallback(() => {
    if (!localStorage.getItem("token")) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      localStorage.removeItem("token");
      nav("/login?motiv=inactivitate");
    }, INACTIVITY_MS);
  }, [nav]);

  useEffect(() => {
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      clearTimeout(timer.current);
    };
  }, [reset]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

function App() {
  return (
    <Router>
      <AutoLogout />
      <ScrollToTop />
      <CookieBanner />
      <WhatsAppWidget />
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* rute protejate */}
        <Route
          path="/dashboard"
          element={
            <AuthGate>
              <Dashboard />
            </AuthGate>
          }
        />
        <Route
          path="/profile"
          element={
            <AuthGate>
              <Profile />
            </AuthGate>
          }
        />

        <Route path="/termeni" element={<Termeni />} />
        <Route path="/metodologie" element={<Metodologie />} />
        <Route path="/confidentialitate" element={<Confidentialitate />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/cgb" element={<Admin />} />

        {/* Blog public */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Blog admin — protejat */}
        <Route
          path="/blog-admin"
          element={
            <AuthGate>
              <BlogAdmin />
            </AuthGate>
          }
        />

        {/* Pagina confirmare plată */}
        <Route
          path="/payment-success"
          element={
            <AuthGate>
              <PaymentSuccess />
            </AuthGate>
          }
        />

        {/* Istoricul rapoartelor */}
        <Route
          path="/rapoartele-mele"
          element={
            <AuthGate>
              <MyReports />
            </AuthGate>
          }
        />

        {/* Calculatoare fiscale */}
        <Route path="/calculatoare" element={<Calculatoare />} />
        <Route path="/calculatoare/salariu" element={<CalculatorSalariu />} />
        <Route path="/calculatoare/dividende" element={<CalculatorDividende />} />
        <Route path="/calculatoare/micro-vs-srl" element={<CalculatorMicroSRL />} />
        <Route path="/calculatoare/tva" element={<CalculatorTVA />} />

        {/* alias vechi /app → /dashboard */}
        <Route path="/app" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
